import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ClipboardCopy, ChevronLeft } from "lucide-react";
import API from "../../../util/API";
import ADMIN_PATHS from "../ADMIN_PATHS";
import AdminPageShell from "../components/AdminPageShell";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const formatOrderMoney = (amount, currency = "USD") => {
  const number = Number(amount);
  const code = String(currency || "USD").trim().toUpperCase();
  if (!Number.isFinite(number)) return "-";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
    }).format(number);
  } catch {
    return `${code} ${number.toFixed(2)}`;
  }
};

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const FieldRow = ({ label, value, mono, title }) => (
  <div className="flex items-start justify-between gap-4 py-2">
    <div className="text-xs font-black uppercase tracking-wide text-slate-400 shrink-0 w-[140px]">
      {label}
    </div>
    <div
      className={cx(
        "text-sm text-slate-800 min-w-0 text-right break-words",
        mono ? "font-mono text-[12px]" : ""
      )}
      title={title || (typeof value === "string" ? value : undefined)}
    >
      {value ?? "—"}
    </div>
  </div>
);

const toDisplayName = (u) =>
  u?.name || [u?.firstName, u?.lastName].filter(Boolean).join(" ") || u?.username || "—";

const initialsFrom = (u) => {
  const name = String(toDisplayName(u) || "").trim();
  if (!name || name === "—") return "U";
  const parts = name.split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || "U";
  const b = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
  return (a + b).toUpperCase();
};

export default function UserDetails() {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("id");

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [tab, setTab] = useState("account");
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);

  useEffect(() => {
    const run = async () => {
      if (!userId) {
        setUser(null);
        setError("Missing user id");
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const res = await API.get(`/user/${encodeURIComponent(userId)}`);
        setUser(res?.data?.data || null);
      } catch (e) {
        setUser(null);
        setError(e?.response?.data?.message || e?.message || "Failed to load user");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [userId]);

  useEffect(() => {
    const run = async () => {
      if (!userId) {
        setOrders([]);
        setOrdersError(null);
        return;
      }
      try {
        setOrdersLoading(true);
        setOrdersError(null);
        const res = await API.get(`/order/admin/user/${encodeURIComponent(userId)}`);
        const payload = res?.data;
        const list = Array.isArray(payload) ? payload : payload?.data;
        setOrders(Array.isArray(list) ? list : []);
      } catch (e) {
        setOrders([]);
        setOrdersError(e?.response?.data?.message || e?.message || "Failed to load orders");
      } finally {
        setOrdersLoading(false);
      }
    };
    run();
  }, [userId]);

  const provider = useMemo(() => {
    if (!user) return "—";
    if (user.googleId) return "Google";
    if (user.password) return "Email";
    return "Email";
  }, [user]);

  const settings = useMemo(
    () => (user?.settings && typeof user.settings === "object" ? user.settings : {}),
    [user?.settings]
  );
  const settingsPretty = useMemo(() => JSON.stringify(settings || {}, null, 2), [settings]);
  const userPretty = useMemo(() => JSON.stringify(user || {}, null, 2), [user]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ""));
    } catch {
      // ignore
    }
  };

  return (
    <AdminPageShell
      title="User Details"
      description={loading ? "Loading user…" : error ? error : user ? toDisplayName(user) : "Select a user"}
      actions={
        <Link
          to={ADMIN_PATHS.USERS}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl border border-slate-200 bg-white text-slate-800 text-sm font-semibold hover:bg-slate-50 transition-colors"
        >
          <ChevronLeft size={16} />
          Back
        </Link>
      }
    >
      {!user && !loading ? (
        <Card className="p-6">
          <div className="text-sm text-slate-700">
            {error || "No user loaded."}
          </div>
        </Card>
      ) : null}

      {user ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <Card className="lg:col-span-4 p-5">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center font-black">
                {initialsFrom(user)}
              </div>
              <div className="min-w-0">
                <div className="text-lg font-black text-slate-900 truncate">{toDisplayName(user)}</div>
                <div className="text-sm text-slate-600 truncate">{user?.email || "—"}</div>
              </div>
            </div>

            <div className="mt-5 border-t border-slate-100 pt-4">
              <FieldRow label="Role" value={user?.role || "—"} />
              <FieldRow label="Provider" value={provider} />
              <FieldRow label="User ID" value={user?._id || "—"} mono />
              <FieldRow
                label="Created"
                value={user?.createdAt ? new Date(user.createdAt).toLocaleString() : "—"}
              />
            </div>

            <div className="mt-5">
              <div className="text-xs font-black uppercase tracking-wide text-slate-400">Quick Actions</div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => copy(user?._id)}
                  className="px-3 py-2 rounded-2xl border border-slate-200 bg-white text-slate-800 text-sm font-semibold hover:bg-slate-50 transition-colors"
                >
                  Copy ID
                </button>
                <button
                  type="button"
                  onClick={() => copy(user?.email)}
                  className="px-3 py-2 rounded-2xl border border-slate-200 bg-white text-slate-800 text-sm font-semibold hover:bg-slate-50 transition-colors"
                >
                  Copy Email
                </button>
              </div>
            </div>
          </Card>

          <div className="lg:col-span-8 space-y-4">
            <Card className="p-2">
              <div className="flex flex-wrap gap-2">
                {[
                  { key: "account", label: "Account Information" },
                  { key: "orders", label: "Order Information" },
                  { key: "settings", label: "Settings" },
                  { key: "debug", label: "Debug" }
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={cx(
                      "px-4 py-2 rounded-2xl text-sm font-semibold transition-colors border",
                      tab === t.key
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-800 border-slate-200 hover:bg-slate-50"
                    )}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </Card>

            {tab === "account" ? (
              <Card className="p-5">
                <div>
                  <div className="text-sm font-black tracking-wide text-slate-900">Account Information</div>
                  <div className="text-xs text-slate-500">What the user sees on their profile</div>
                </div>
                <div className="mt-4 divide-y divide-slate-100">
                  <FieldRow label="Name" value={toDisplayName(user)} />
                  <FieldRow label="Username" value={user?.username || "—"} />
                  <FieldRow label="First Name" value={user?.firstName || "—"} />
                  <FieldRow label="Last Name" value={user?.lastName || "—"} />
                  <FieldRow label="Email" value={user?.email || "—"} />
                  <FieldRow label="Phone" value={user?.phone || "—"} />
                  <FieldRow label="Address" value={user?.address || "—"} />
                </div>
              </Card>
            ) : null}

            {tab === "orders" ? (
              <Card className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-black tracking-wide text-slate-900">Order Information</div>
                    <div className="text-xs text-slate-500">User's order history (admin view)</div>
                  </div>
                </div>

                {ordersLoading ? (
                  <div className="mt-4 text-sm text-slate-600">Loading orders…</div>
                ) : ordersError ? (
                  <div className="mt-4 text-sm text-rose-600">{ordersError}</div>
                ) : (
                  <>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Card className="p-4 bg-slate-50 border-slate-100">
                        <div className="text-[11px] font-black uppercase tracking-wide text-slate-400">Total Orders</div>
                        <div className="mt-1 text-xl font-black text-slate-900">{orders.length}</div>
                      </Card>
                      <Card className="p-4 bg-slate-50 border-slate-100">
                        <div className="text-[11px] font-black uppercase tracking-wide text-slate-400">Paid</div>
                        <div className="mt-1 text-xl font-black text-slate-900">
                          {orders.filter((o) => o?.isPaid).length}
                        </div>
                      </Card>
                      <Card className="p-4 bg-slate-50 border-slate-100">
                        <div className="text-[11px] font-black uppercase tracking-wide text-slate-400">Delivered</div>
                        <div className="mt-1 text-xl font-black text-slate-900">
                          {orders.filter((o) => o?.isDelivered).length}
                        </div>
                      </Card>
                    </div>

                    <div className="mt-4 overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                            <th className="py-3 pr-4">Order</th>
                            <th className="py-3 pr-4">Created</th>
                            <th className="py-3 pr-4">Total</th>
                            <th className="py-3 pr-4">Payment</th>
                            <th className="py-3 pr-4">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {orders.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="py-4 text-slate-600">
                                No orders found for this user.
                              </td>
                            </tr>
                          ) : (
                            orders.map((o) => (
                              <tr key={o?._id} className="align-top">
                                <td className="py-3 pr-4">
                                  <div className="font-mono text-[12px] text-slate-900 break-all">{o?._id}</div>
                                  <button
                                    type="button"
                                    onClick={() => copy(o?._id)}
                                    className="mt-1 inline-flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-slate-900"
                                  >
                                    <ClipboardCopy size={14} /> Copy
                                  </button>
                                </td>
                                <td className="py-3 pr-4 text-slate-700">
                                  {o?.createdAt ? new Date(o.createdAt).toLocaleString() : "—"}
                                </td>
                                <td className="py-3 pr-4 text-slate-900 font-semibold">
                                  <div>
                                    {formatOrderMoney(
                                      o?.totalPrice,
                                      o?.paidCurrency || o?.currency || o?.items?.[0]?.currency || "USD"
                                    )}
                                  </div>
                                  {Number.isFinite(Number(o?.accountingUsd?.amount ?? o?.baseTotalPrice)) ? (
                                    <div className="mt-0.5 text-xs font-medium text-slate-500">
                                      {formatOrderMoney(
                                        Number(o?.accountingUsd?.amount ?? o?.baseTotalPrice),
                                        "USD"
                                      )}{" "}
                                      {o?.accountingUsd?.classification === "current_rate_estimate"
                                        ? "estimate"
                                        : o?.accountingUsd?.classification === "manual_correction"
                                          ? "corrected"
                                          : "base"}
                                    </div>
                                  ) : null}
                                </td>
                                <td className="py-3 pr-4 text-slate-700">{o?.paymentMethod || "—"}</td>
                                <td className="py-3 pr-4">
                                  <div className="flex flex-wrap gap-2">
                                    <span
                                      className={cx(
                                        "px-2.5 py-1 rounded-full text-xs font-black border",
                                        o?.isPaid
                                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                          : "bg-amber-50 text-amber-700 border-amber-200"
                                      )}
                                    >
                                      {o?.isPaid ? "Paid" : "Unpaid"}
                                    </span>
                                    <span
                                      className={cx(
                                        "px-2.5 py-1 rounded-full text-xs font-black border",
                                        o?.isDelivered
                                          ? "bg-sky-50 text-sky-700 border-sky-200"
                                          : "bg-slate-50 text-slate-600 border-slate-200"
                                      )}
                                    >
                                      {o?.isDelivered ? "Delivered" : "Not Delivered"}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </Card>
            ) : null}

            {tab === "settings" ? (
              <Card className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-black tracking-wide text-slate-900">Settings</div>
                    <div className="text-xs text-slate-500">User preferences stored in DB</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => copy(settingsPretty)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-slate-200 bg-white text-slate-800 text-sm font-semibold hover:bg-slate-50 transition-colors"
                  >
                    <ClipboardCopy size={16} className="text-slate-600" />
                    Copy
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Card className="p-4 bg-slate-50 border-slate-100">
                    <div className="text-[11px] font-black uppercase tracking-wide text-slate-400">Currency</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{settings?.currency || "USD"}</div>
                  </Card>
                  <Card className="p-4 bg-slate-50 border-slate-100">
                    <div className="text-[11px] font-black uppercase tracking-wide text-slate-400">Ship To</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {settings?.shipto || settings?.country || "—"}
                    </div>
                  </Card>
                  <Card className="p-4 bg-slate-50 border-slate-100">
                    <div className="text-[11px] font-black uppercase tracking-wide text-slate-400">Country</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{settings?.country || "—"}</div>
                  </Card>
                  <Card className="p-4 bg-slate-50 border-slate-100">
                    <div className="text-[11px] font-black uppercase tracking-wide text-slate-400">Language</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{settings?.language || "—"}</div>
                  </Card>
                  <Card className="p-4 bg-slate-50 border-slate-100">
                    <div className="text-[11px] font-black uppercase tracking-wide text-slate-400">Theme</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{settings?.theme || "—"}</div>
                  </Card>
                </div>
              </Card>
            ) : null}

            {tab === "debug" ? (
              <Card className="p-5">
                <details>
                  <summary className="cursor-pointer select-none">
                    <div className="text-sm font-black tracking-wide text-slate-900">Debug (Raw JSON)</div>
                    <div className="text-xs text-slate-500">Expand only when you need to inspect raw data</div>
                  </summary>

                  <div className="mt-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-black tracking-wide text-slate-900">Raw User</div>
                        <div className="text-xs text-slate-500">Full user JSON</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => copy(userPretty)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-slate-200 bg-white text-slate-800 text-sm font-semibold hover:bg-slate-50 transition-colors"
                      >
                        <ClipboardCopy size={16} className="text-slate-600" />
                        Copy
                      </button>
                    </div>
                    <pre className="mt-3 p-3 rounded-2xl bg-slate-900 text-slate-100 text-xs overflow-x-auto">
{userPretty}
                    </pre>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-black tracking-wide text-slate-900">Raw Settings</div>
                        <div className="text-xs text-slate-500">Settings JSON</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => copy(settingsPretty)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-slate-200 bg-white text-slate-800 text-sm font-semibold hover:bg-slate-50 transition-colors"
                      >
                        <ClipboardCopy size={16} className="text-slate-600" />
                        Copy
                      </button>
                    </div>
                    <pre className="mt-3 p-3 rounded-2xl bg-slate-900 text-slate-100 text-xs overflow-x-auto">
{settingsPretty}
                    </pre>
                  </div>
                </details>
              </Card>
            ) : null}
          </div>
        </div>
      ) : null}
    </AdminPageShell>
  );
}
